import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { d as defineCustomElement$3 } from './close.js';
import { d as defineCustomElement$2 } from './dropdown-popup.js';

/** Default minimal offset between dropdown and window border */
const BORDER_OFFSET_DEFAULT = 20;
/** Default hover debounce time */
const HOVER_DEBOUNCE_DEFAULT = 300;
/** Default offset between anchor element and dropdown */
const DROPDOWN_OFFSET_DEFAULT = 25;
/** Default pointer height (pixels) */
const POINTER_HEIGHT_DEFAULT = 20;
/** Default pointer width (pixels) */
const POINTER_WIDTH_DEFAULT = 34;
/** Default pointer offset from dropdown border */
const POINTER_OFFSET_DEFAULT = 20;
const ISPUIDropdown = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.close = createEvent(this, "close", 3);
    this.open = createEvent(this, "open", 3);
    this.openingStart = createEvent(this, "openingStart", 3);
    this.closingStart = createEvent(this, "closingStart", 3);
    this.closeClick = createEvent(this, "closeClick", 3);
    this.observer = new MutationObserver(mutations => {
      mutations.find(mutation => {
        if (mutation.addedNodes || mutation.removedNodes) {
          this.updateAnchor();
        }
      });
    });
    /** Hover debounce time */
    this.hoverDebounceDefault = 300;
    /** Default offset between anchor element and dropdown */
    this.defaultDropdownOffset = 25;
    /** Dropdown visibility status */
    this.isVisible = false;
    /** List of refs to the content elements */
    this.contentComponents = [];
    /** ref to click handler method for adding and removing as event listener */
    this.clickHandler = this.toggleDropdown.bind(this);
    /** ref to mouseleave handler method for adding and removing as event listener */
    this.mouseLeaveHandler = this.handleMouseLeave.bind(this);
    /** ref to mouseenter handler method for adding and removing as event listener */
    this.mouseEnterHandler = this.handleMouseEnter.bind(this);
    /** ref to window scroll and resize handle method for adding and removing as event listener */
    this.windowScrollAndResizeHandler = this.resetDropdown.bind(this);
    /** ref to document click handle method for adding and removing as event listener */
    this.documentClickHandler = this.handleDocumentClick.bind(this);
    /** ref to scroll event hadle method  */
    this.scrollHandler = this.handleContainerScroll.bind(this);
    /** Flag for handle close button existence */
    this.isClose = true;
    /** Flag for handle pointer existence */
    this.hasPointer = true;
    /** Close dropdown on click */
    this.closeAfterClick = false;
    /** Flag for freeze fropdown direction */
    this.strictDirection = false;
    /** Flag for disable dropdown showing */
    this.disabled = false;
    /** Dropdown open type: click | hover */
    this.type = 'click';
    /** Dropdown direction */
    this.direction = 'bottom';
    /** Dropdown alignment (note: "start-auto" currently works only for "left" and "right" direction)*/
    this.alignment = 'auto';
    /** Offset between anchor element and dropdown */
    this.offset = DROPDOWN_OFFSET_DEFAULT;
    /** Timeout debounce before show dropdown popup */
    this.hoverDebounce = HOVER_DEBOUNCE_DEFAULT;
    /** Minimal offset between dropdown and window border */
    this.borderOffset = BORDER_OFFSET_DEFAULT;
    /** Pointer height (pixels) */
    this.pointerHeight = POINTER_HEIGHT_DEFAULT;
    /** Pointer width (pixels) */
    this.pointerWidth = POINTER_WIDTH_DEFAULT;
    /** Pointer offset from dropdown border */
    this.pointerOffset = POINTER_OFFSET_DEFAULT;
    /** Close dropdown on outside click */
    this.closeOnOuterClick = true;
    /** offset for start-auto alignment */
    this.startAutoOffset = POINTER_OFFSET_DEFAULT;
    /** State for dropdown alignment */
    this.currentAlignment = 'auto';
  }
  updateAnchor() {
    for (const child of Array.from(this.hostEl.children)) {
      if (child.slot === 'anchor') {
        this.anchorEl = child;
      }
    }
  }
  /**
   * Validate value of hoverDebounce prop
   * @param newHoverDebounce - timeout debounce before show dropdown popup
   */
  validateHoverDebounce(newHoverDebounce) {
    if (isNaN(newHoverDebounce)) {
      return;
    }
    this.hoverDebounce = newHoverDebounce;
  }
  /**
   * Update event listeners on the anchor element
   * @param type - type of the action
   */
  updateToggleTriggerListeners(type) {
    const host = this.hostEl;
    if (!host) {
      return;
    }
    if (type === 'clear') {
      host.removeEventListener('mouseleave', this.mouseLeaveHandler);
      host.removeEventListener('mouseenter', this.mouseEnterHandler);
      host.removeEventListener('click', this.clickHandler);
    }
    else if (this.disabled) {
      this.clearHoverTimeout();
      host.removeEventListener('mouseenter', this.mouseEnterHandler);
      host.removeEventListener('click', this.clickHandler);
    }
    else if (type === 'click') {
      host.removeEventListener('mouseleave', this.mouseLeaveHandler);
      host.removeEventListener('mouseenter', this.mouseEnterHandler);
      host.addEventListener('click', this.clickHandler);
    }
    else if (type === 'hover') {
      host.addEventListener('mouseleave', this.mouseLeaveHandler);
      host.addEventListener('mouseenter', this.mouseEnterHandler);
      host.removeEventListener('click', this.clickHandler);
    }
  }
  /**
   * Set event listeners according disabled state
   */
  setDisabledTriggers() {
    this.updateToggleTriggerListeners(this.type);
  }
  /**
   * Validate dropdown direction
   * @param newDirection - dropdown direction
   */
  validateDirection(newDirection) {
    if (['bottom', 'right', 'left', 'top'].includes(newDirection)) {
      this.currentDirection = newDirection;
    }
  }
  clearHoverTimeout() {
    if (this.hoverTimeout) {
      window.clearTimeout(this.hoverTimeout);
    }
  }
  setHoverTimeout(callback) {
    if (this.hoverDebounce >= 0) {
      this.hoverTimeout = window.setTimeout(callback, this.hoverDebounce);
    }
  }
  /**
   * LIFECYCLE
   *
   * Validate props
   */
  componentWillLoad() {
    this.updateToggleTriggerListeners(this.type);
    this.validateHoverDebounce(this.hoverDebounce);
    this.validateDirection(this.direction);
  }
  /**
   * LIFECYCLE
   *
   * Form dropdown content
   */
  componentDidLoad() {
    var _a, _b;
    (_a = this.instance) === null || _a === void 0 ? void 0 : _a.remove();
    this.updateAnchor();
    for (const child of Array.from(this.hostEl.children)) {
      if (child === this.instance || child.slot === 'anchor') {
        continue;
      }
      else {
        this.contentComponents.push(child);
        child.remove();
      }
    }
    (_b = this.instance) === null || _b === void 0 ? void 0 : _b.addEventListener('animationend', this.listenDropdownAnimation.bind(this));
    const scrollElems = document.querySelectorAll(this.scrollSelector);
    scrollElems === null || scrollElems === void 0 ? void 0 : scrollElems.forEach(scrollElem => {
      scrollElem.addEventListener('scroll', this.scrollHandler);
    });
    this.scrollSelectorElements = scrollElems;
  }
  /**
   * LIFECYCLE
   *
   * Add fresh event listeners for the anhor
   */
  connectedCallback() {
    this.observer.observe(this.hostEl, {
      childList: true,
    });
    this.updateToggleTriggerListeners(this.type);
  }
  /**
   * LIFECYCLE
   *
   * Clear event listeners
   */
  disconnectedCallback() {
    var _a;
    this.observer.disconnect();
    if (this.instance) {
      this.instance.removeEventListener('animationend', this.listenDropdownAnimation.bind(this));
      this.instance.remove();
    }
    this.clearHoverTimeout();
    (_a = this.scrollSelectorElements) === null || _a === void 0 ? void 0 : _a.forEach(scrollSelectorElement => {
      scrollSelectorElement.removeEventListener('scroll', this.scrollHandler);
    });
    this.updateToggleTriggerListeners('clear');
  }
  /**
   * Handle scroll event scrollSelector element to update dropdown position
   */
  handleContainerScroll() {
    this.setupDropdown();
  }
  /**
   * Handle mouse enter on anchor element
   */
  handleMouseEnter() {
    this.clearHoverTimeout();
    if (!this.isVisible) {
      this.setHoverTimeout(() => this.showDropdown());
    }
  }
  handleCloseClick() {
    this.closeClick.emit();
    this.closeDropdown();
  }
  /**
   * Handle mouse leave on anchor element
   * @param isOnPopup - is event fired on the popup instance (not on the anchor)
   */
  handleMouseLeave(isOnPopup = false) {
    if (isOnPopup && this.type === 'click') {
      return;
    }
    this.clearHoverTimeout();
    if (this.isVisible) {
      this.setHoverTimeout(() => this.closeDropdown());
    }
  }
  /**
   * Reset dropdown
   */
  resetDropdown() {
    if (this.resetTimeout) {
      window.clearTimeout(this.resetTimeout);
    }
    this.resetTimeout = window.setTimeout(() => {
      if (this.isVisible && this.instance && this.anchorEl) {
        this.setupDropdown();
      }
    }, 100);
  }
  /**
   * Handle document clicks
   * @param event - click event
   */
  handleDocumentClick(event) {
    if (!this.closeOnOuterClick) {
      return;
    }
    if (this.instance) {
      const target = event.target;
      const wasClickOnChildPopup = this.isDescendant(target);
      const canCloseAfterClick = this.closeAfterClick && this.instance.contains(target);
      const isClickedOutside = !this.instance.contains(target) && !this.hostEl.contains(target) && !wasClickOnChildPopup;
      if (canCloseAfterClick || isClickedOutside) {
        this.closeDropdown();
      }
    }
  }
  /** Is element a descendant for our popup instance, counting nested dropdowns inside our popup */
  isDescendant(el) {
    var _a, _b;
    if (!this.instance) {
      return false;
    }
    let closestPopup = el.closest('ispui-dropdown-popup');
    while (closestPopup) {
      if (closestPopup === this.instance) {
        return true;
      }
      closestPopup = (_b = (_a = closestPopup === null || closestPopup === void 0 ? void 0 : closestPopup.ISPUIDropdownEl) === null || _a === void 0 ? void 0 : _a.hostEl) === null || _b === void 0 ? void 0 : _b.closest('ispui-dropdown-popup');
    }
    return false;
  }
  /**
   * Show dropdown method
   */
  async showDropdown() {
    var _a;
    this.isVisible = true;
    (_a = this.instance) === null || _a === void 0 ? void 0 : _a.classList.remove('ispui-dropdown_closing');
    if (this.instance) {
      this.openingStart.emit();
      const parentElement = document.querySelector(this.parentSelector) || document.body;
      parentElement.appendChild(this.instance);
      this.contentComponents.forEach(com => { var _a, _b; return (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.querySelector('.ispui-dropdown-popup__container')) === null || _b === void 0 ? void 0 : _b.appendChild(com); });
      await this.setupDropdown();
      this.toggleDropdownDocumentListeners(true);
      this.open.emit();
    }
  }
  /**
   * Get popup instance
   */
  async getPopup() {
    return this.instance;
  }
  /**
   * Close dropdown method
   */
  async closeDropdown() {
    var _a;
    if (this.instance) {
      this.closingStart.emit();
      (_a = this.instance) === null || _a === void 0 ? void 0 : _a.classList.add('ispui-dropdown_closing');
    }
  }
  /**
   * Set pointer position calc function
   * @param callback - callback for calculate pointer position
   */
  async setPointerPositionCallback(callback) {
    this.pointerPositionCallback = callback;
  }
  /**
   * Calculate height and position for dropdown instance
   */
  async setupDropdown() {
    if (!this.instance || !this.anchorEl) {
      return;
    }
    // nail popup to the container's top left corner to avoid it's scroll height increasing
    this.instance.style.top = '0';
    this.instance.style.left = '0';
    const anchorRect = this.anchorEl.getBoundingClientRect();
    this.setMinHeight(this.instance);
    this.setDropdownDirection(this.instance, anchorRect);
    this.setDropdownAlignment(this.instance, anchorRect);
    const maxWidth = this.resetAndCalculateMaxWidth(this.instance, anchorRect, this.currentAlignment);
    this.instance.style.maxWidth = `${maxWidth}px`;
    const maxHeight = this.calculateMaxHeight(this.instance, anchorRect, this.currentAlignment);
    this.instance.style.maxHeight = `${maxHeight}px`;
    this.setDropdownPosition(this.instance, anchorRect);
    this.setPointerPosition(this.instance, anchorRect);
  }
  /**
   * Toggle global document and window event listeners for adjusting dropdown position or hiding it
   * @param isAdd - add listeners, if not - remove them
   */
  toggleDropdownDocumentListeners(isAdd = false) {
    if (isAdd) {
      window.addEventListener('resize', this.windowScrollAndResizeHandler);
      window.addEventListener('scroll', this.windowScrollAndResizeHandler);
      document.addEventListener('click', this.documentClickHandler);
    }
    else {
      window.removeEventListener('resize', this.windowScrollAndResizeHandler);
      window.removeEventListener('scroll', this.windowScrollAndResizeHandler);
      document.removeEventListener('click', this.documentClickHandler);
    }
  }
  /**
   * Remove dropdown after fade animation out
   * @param e - animation event
   */
  listenDropdownAnimation(e) {
    if (!this.instance) {
      return;
    }
    if (e.type === 'animationend' && e.animationName === 'fade-out') {
      this.isVisible = false;
      this.contentComponents.forEach(com => {
        com.remove();
      });
      this.instance.remove();
      this.close.emit();
      this.toggleDropdownDocumentListeners();
    }
  }
  /**
   * Toggle dropdown state
   */
  toggleDropdown() {
    if (!this.isVisible) {
      this.showDropdown();
    }
    else {
      this.closeDropdown();
    }
  }
  /**
   * Calculate dropdown direction
   *
   * @param instance - dropdown htmlelement instance
   * @param anchorRect - DOMrect of anchor element
   */
  setDropdownDirection(instance, anchorRect) {
    var _a;
    if (this.strictDirection) {
      this.currentDirection = this.direction;
      return;
    }
    const offsets = this.offset + this.borderOffset;
    const popupAreaX = instance.clientWidth + offsets;
    const popupAreaY = instance.clientHeight + offsets;
    const space = {
      top: anchorRect.top,
      bottom: document.documentElement.clientHeight - anchorRect.bottom,
      left: anchorRect.left,
      right: document.documentElement.clientWidth - anchorRect.right,
    };
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      const altParentRect = altParent.getBoundingClientRect();
      space.top = anchorRect.top - altParentRect.top + altParent.scrollTop;
      space.bottom = altParentRect.bottom - anchorRect.bottom + (altParent.scrollHeight - altParent.scrollTop - altParent.clientHeight);
      space.left = anchorRect.left - altParentRect.left + altParent.scrollLeft;
      space.right = altParentRect.right - anchorRect.right + (altParent.scrollWidth - altParent.scrollLeft - altParent.clientWidth);
    }
    const isSpaceEnough = (dir) => ({
      top: space.top >= popupAreaY,
      bottom: space.bottom >= popupAreaY,
      left: space.left >= popupAreaX,
      right: space.right >= popupAreaX,
    }[dir]);
    if (this.directionOrder && ((_a = this.directionOrder) === null || _a === void 0 ? void 0 : _a.length) > 0) {
      let maxDirectionSpace = space[this.direction];
      let maxDirection = this.direction;
      for (const orderDirection of this.directionOrder) {
        const directionSpace = space[orderDirection];
        if (directionSpace > maxDirectionSpace) {
          maxDirection = orderDirection;
          maxDirectionSpace = directionSpace;
        }
      }
      this.currentDirection = maxDirection;
    }
    else {
      const altDirections = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
      const altDirection = altDirections[this.direction];
      if (isSpaceEnough(this.direction) || space[this.direction] >= space[altDirection]) {
        this.currentDirection = this.direction;
      }
      else {
        this.currentDirection = altDirection;
      }
    }
  }
  /**
   * Calculate dropdown alignment
   *
   * @param instance - dropdown htmlelement instance
   * @param anchorRect - DOMRect of anchor element
   */
  setDropdownAlignment(instance, anchorRect) {
    const dropdownStyles = getComputedStyle(instance);
    const minWidth = parseInt(dropdownStyles.minWidth) || 200;
    const minHeight = parseInt(dropdownStyles.minWidth);
    let alignmentToUse = this.alignment;
    if (this.alignmentOrder) {
      if (this.currentDirection === 'top' || this.currentDirection === 'bottom') {
        const maxWidthInAlignment = this.resetAndCalculateMaxWidth(instance, anchorRect, alignmentToUse);
        if (maxWidthInAlignment <= minWidth) {
          for (const al of this.alignmentOrder) {
            const maxWidthInAl = this.resetAndCalculateMaxWidth(instance, anchorRect, al);
            if (maxWidthInAl > minWidth) {
              alignmentToUse = al;
              break;
            }
          }
        }
      }
      else {
        let maxHeightInAlignment = this.calculateMaxHeight(instance, anchorRect, alignmentToUse);
        if (maxHeightInAlignment <= minHeight) {
          for (const alignment of this.alignmentOrder) {
            maxHeightInAlignment = this.calculateMaxHeight(instance, anchorRect, alignment);
            if (maxHeightInAlignment >= minHeight) {
              alignmentToUse = alignment;
              break;
            }
          }
        }
      }
    }
    this.currentAlignment = alignmentToUse;
  }
  /**
   * Calculate dropdown position
   *
   * @param instance - dropdown htmlelement instance
   * @param anchorRect - DOMRect of anchor element
   */
  setDropdownPosition(instance, anchorRect) {
    instance.style.transform = '';
    let anchorTop = window.scrollY + anchorRect.top;
    let anchorLeft = window.scrollX + anchorRect.left;
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      const altParentRect = altParent === null || altParent === void 0 ? void 0 : altParent.getBoundingClientRect();
      anchorTop = anchorRect.top - altParentRect.top + altParent.scrollTop;
      anchorLeft = anchorRect.left - altParentRect.left + altParent.scrollLeft;
    }
    switch (this.currentDirection) {
      case 'top':
        instance.style.top = `${anchorTop - this.offset}px`;
        instance.style.transform = 'translateY(-100%)';
        this.setHorizontalAlign(anchorRect);
        break;
      case 'bottom':
        instance.style.top = `${anchorTop + anchorRect.height + this.offset}px`;
        this.setHorizontalAlign(anchorRect);
        break;
      case 'left':
        instance.style.left = `${anchorLeft - this.offset}px`;
        instance.style.transform = 'translateX(-100%)';
        this.calculateVerticalAlign(anchorRect);
        break;
      case 'right':
        instance.style.left = `${anchorLeft + anchorRect.width + this.offset}px`;
        this.calculateVerticalAlign(anchorRect);
        break;
    }
  }
  /**
   * Set dropdown height
   */
  setHeight() {
    if (this.instance) {
      this.instance.style.height = `${this.instance.getBoundingClientRect().height}px`;
    }
  }
  /**
   * Calculate dropdown max width
   *
   * @param instance - dropdown instance
   * @param anchorRect - DOMrect of anchor element
   * @param alignment - alignment
   * @returns max width in px
   */
  resetAndCalculateMaxWidth(instance, anchorRect, alignment) {
    instance.style.maxWidth = '';
    const dropdownStyles = getComputedStyle(instance);
    const minWidth = parseInt(dropdownStyles.minWidth);
    let maxWidth = parseInt(dropdownStyles.maxWidth);
    let parentWidth = document.documentElement.clientWidth;
    let anchorLeft = anchorRect.left;
    let anchorRight = anchorRect.right;
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      const altParentRect = altParent.getBoundingClientRect();
      parentWidth = altParent.scrollWidth;
      anchorLeft = anchorRect.left - altParentRect.left + altParent.scrollLeft;
      anchorRight = anchorLeft + anchorRect.width;
    }
    if (this.currentDirection === 'left') {
      maxWidth = Math.min(maxWidth, anchorLeft - this.borderOffset - this.offset);
    }
    else if (this.currentDirection === 'right') {
      maxWidth = Math.min(maxWidth, parentWidth - this.borderOffset - this.offset - anchorRight);
    }
    else {
      switch (alignment) {
        case 'start':
          maxWidth = Math.min(maxWidth, parentWidth - anchorLeft - this.borderOffset);
          break;
        case 'middle':
          maxWidth = Math.min(maxWidth, (Math.min(anchorLeft + anchorRect.width / 2, parentWidth - anchorLeft - anchorRect.width / 2) - this.borderOffset) * 2);
          break;
        case 'end':
          maxWidth = Math.min(maxWidth, anchorRight - this.borderOffset);
          break;
        case 'auto':
          maxWidth = Math.min(maxWidth, parentWidth - this.borderOffset * 2);
          break;
        default:
          break;
      }
    }
    return Math.max(maxWidth, minWidth);
  }
  /**
   * Calculate dropdown min height
   *
   * @param instance - dropdown htmlelement instance
   */
  setMinHeight(instance) {
    const currentMinHeight = parseInt(getComputedStyle(instance).minHeight) || 0;
    let minHeight = 0;
    for (const child of Array.from(instance.children)) {
      const childStyle = getComputedStyle(child);
      if (parseInt(childStyle.minHeight)) {
        minHeight += parseInt(childStyle.minHeight);
      }
      else {
        minHeight += parseInt(childStyle.height);
      }
    }
    instance.style.minHeight = `${Math.max(currentMinHeight, minHeight)}px`;
  }
  /**
   * Calculate dropdown max height
   *
   * @param instance - dropdown html element instance
   * @param anchorRect - DOMrect of anchor element
   * @param alignment - alignment
   */
  calculateMaxHeight(instance, anchorRect, alignment) {
    instance.style.maxHeight = '';
    const dropdownStyles = getComputedStyle(instance);
    const minHeight = parseInt(dropdownStyles.minHeight);
    let maxHeight = parseInt(dropdownStyles.maxHeight);
    let parentHeight = document.documentElement.clientHeight;
    let anchorParentTop = anchorRect.top;
    let anchorParentBottom = anchorRect.bottom;
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      parentHeight = altParent.clientHeight;
      const altParentRect = altParent.getBoundingClientRect();
      anchorParentTop = anchorRect.top - altParentRect.top;
      anchorParentBottom = anchorParentTop + anchorRect.height;
    }
    if (this.currentDirection === 'top') {
      maxHeight = Math.min(maxHeight, anchorParentTop - this.borderOffset - this.offset);
    }
    else if (this.currentDirection === 'bottom') {
      maxHeight = Math.min(maxHeight, parentHeight - anchorParentBottom - this.borderOffset - this.offset);
    }
    else {
      switch (alignment) {
        case 'start':
          maxHeight = Math.min(maxHeight, parentHeight - anchorParentTop - this.borderOffset);
          break;
        case 'middle':
          maxHeight = Math.min(maxHeight, (Math.min(anchorParentTop + anchorRect.height / 2, parentHeight - anchorParentTop - anchorRect.height / 2) -
            this.borderOffset) *
            2);
          break;
        case 'end':
          maxHeight = Math.min(maxHeight, anchorParentBottom - this.borderOffset);
          break;
        case 'auto':
          maxHeight = Math.min(maxHeight, parentHeight - this.borderOffset * 2);
          break;
        case 'start-auto':
          maxHeight = Math.min(maxHeight, parentHeight - this.borderOffset * 2);
          break;
      }
    }
    return Math.max(minHeight, maxHeight);
  }
  /**
   * Calculate dropdown horizontal position from alignment
   *
   * @param anchorRect - DOMRect of anchor element
   */
  setHorizontalAlign(anchorRect) {
    if (!this.instance) {
      return;
    }
    const dropdownRect = this.instance.getBoundingClientRect();
    let parentScrollLeft = window.scrollX;
    let anchorFromParentLeft = anchorRect.left;
    let anchorFromParentRight = anchorRect.right;
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      const altParentRect = altParent.getBoundingClientRect();
      parentScrollLeft = altParent.scrollLeft;
      anchorFromParentLeft = anchorRect.left - altParentRect.left;
      anchorFromParentRight = anchorFromParentLeft + anchorRect.width;
    }
    switch (this.currentAlignment) {
      case 'start':
        this.instance.style.left = `${parentScrollLeft + anchorFromParentLeft}px`;
        break;
      case 'auto':
      case 'middle':
        const anchorCenter = parentScrollLeft + anchorFromParentLeft + anchorRect.width / 2;
        const rightBorder = anchorCenter + dropdownRect.width / 2;
        const leftBorder = anchorCenter - dropdownRect.width / 2;
        if (leftBorder < parentScrollLeft + this.borderOffset) {
          const minLeft = Math.min(parentScrollLeft + this.borderOffset, parentScrollLeft + anchorFromParentRight - (this.pointerWidth / 2 + this.pointerOffset));
          this.instance.style.left = `${minLeft}px`;
        }
        else if (rightBorder > parentScrollLeft + document.documentElement.clientWidth - this.borderOffset) {
          const minRight = Math.max(parentScrollLeft + document.documentElement.clientWidth - this.borderOffset, parentScrollLeft + anchorFromParentLeft + this.pointerWidth / 2 + this.pointerOffset);
          this.instance.style.left = `${minRight - dropdownRect.width}px`;
        }
        else {
          this.instance.style.left = `${leftBorder}px`;
        }
        break;
      case 'end':
        this.instance.style.left = `${parentScrollLeft + anchorFromParentRight - dropdownRect.width}px`;
        break;
      default:
        break;
    }
  }
  /**
   * Calculate dropdown vertical position from alignment
   *
   * @param anchorRect - DOMRect of anchor element
   */
  calculateVerticalAlign(anchorRect) {
    if (!this.instance) {
      return;
    }
    const dropdownRect = this.instance.getBoundingClientRect();
    let parentScrollTop = window.scrollY;
    let anchorTop = anchorRect.top;
    let anchorBottom = anchorRect.bottom;
    const altParent = document.querySelector(this.parentSelector);
    if (altParent) {
      const altParentRect = altParent.getBoundingClientRect();
      parentScrollTop = altParent.scrollTop;
      anchorTop = anchorRect.top - altParentRect.top;
      anchorBottom = anchorTop + anchorRect.height;
    }
    switch (this.currentAlignment) {
      case 'start':
        this.instance.style.top = `${parentScrollTop + anchorTop}px`;
        break;
      case 'auto':
      case 'middle':
        const anchorCenter = parentScrollTop + anchorTop + anchorRect.height / 2;
        const topBorder = anchorCenter - dropdownRect.height / 2;
        const bottomBorder = anchorCenter + dropdownRect.height / 2;
        if (topBorder < parentScrollTop + this.borderOffset) {
          const minTop = Math.min(parentScrollTop + this.borderOffset, parentScrollTop + anchorBottom - (this.pointerWidth / 2 + this.pointerOffset));
          this.instance.style.top = `${minTop}px`;
        }
        else if (bottomBorder > parentScrollTop + document.documentElement.clientHeight - this.borderOffset) {
          const maxBottom = Math.max(parentScrollTop + document.documentElement.clientHeight - this.borderOffset, parentScrollTop + anchorBottom + (this.pointerWidth / 2 + this.pointerOffset));
          this.instance.style.top = `${maxBottom - dropdownRect.height}px`;
        }
        else {
          this.instance.style.top = `${topBorder}px`;
        }
        break;
      case 'end':
        this.instance.style.top = `${parentScrollTop + anchorBottom - dropdownRect.height}px`;
        break;
      case 'start-auto':
        let absoluteBottomLimit;
        let relativeBottomLimit;
        if (altParent) {
          const parentRect = altParent.getBoundingClientRect();
          absoluteBottomLimit = Math.min(document.documentElement.clientHeight + window.scrollY, parentRect.bottom + window.scrollY);
          relativeBottomLimit = Math.min(parentRect.height, absoluteBottomLimit - parentRect.top - window.scrollY) - this.borderOffset;
        }
        else {
          absoluteBottomLimit = document.documentElement.clientHeight + window.scrollY;
          relativeBottomLimit = absoluteBottomLimit - this.borderOffset;
        }
        if (anchorRect.top + dropdownRect.height + window.scrollY - this.startAutoOffset > absoluteBottomLimit - this.borderOffset) {
          this.instance.style.top = `${relativeBottomLimit - dropdownRect.height - Math.min(anchorRect.top - this.borderOffset, 0)}px`;
        }
        else {
          this.instance.style.top = `${parentScrollTop + anchorTop - Math.min(anchorRect.top - this.borderOffset, this.startAutoOffset)}px`;
        }
        break;
    }
  }
  /**
   * Calculate pointer position
   *
   * @param instance - dropdown instance
   * @param anchorRect - DOMRect of anchor element
   */
  setPointerPosition(instance, anchorRect) {
    const dropdownRect = instance.getBoundingClientRect();
    /** Pointer offset from dropdown border */
    const POINTER_OFFSET = 20;
    if (this.pointerPositionCallback) {
      const pos = this.pointerPositionCallback(anchorRect, dropdownRect, this.pointerWidth);
      switch (this.currentDirection) {
        case 'top':
        case 'bottom':
          instance.style.setProperty('--ispui-dropdown__pointer-left', `${pos}px`);
          break;
        case 'left':
        case 'right':
          instance.style.setProperty('--ispui-dropdown__pointer-top', `${pos}px`);
          break;
      }
      return;
    }
    switch (this.currentDirection) {
      case 'top':
      case 'bottom':
        const anchorCenter = anchorRect.left + anchorRect.width / 2;
        let xPos = anchorCenter - dropdownRect.left - this.pointerWidth / 2;
        if (anchorCenter < dropdownRect.left + POINTER_OFFSET + this.pointerWidth / 2) {
          xPos = POINTER_OFFSET;
        }
        else if (anchorCenter > dropdownRect.right - POINTER_OFFSET - this.pointerWidth / 2) {
          xPos = dropdownRect.width - POINTER_OFFSET - this.pointerWidth;
        }
        instance.style.setProperty('--ispui-dropdown__pointer-left', `${xPos}px`);
        break;
      case 'left':
      case 'right':
        const anchorHeightCenter = anchorRect.top + anchorRect.height / 2;
        let yPos = anchorHeightCenter - dropdownRect.top - this.pointerWidth / 2;
        if (anchorHeightCenter < dropdownRect.top + POINTER_OFFSET + this.pointerWidth / 2) {
          yPos = POINTER_OFFSET;
        }
        else if (anchorHeightCenter > dropdownRect.bottom - POINTER_OFFSET - this.pointerWidth / 2) {
          yPos = dropdownRect.height - POINTER_OFFSET - this.pointerWidth;
        }
        instance.style.setProperty('--ispui-dropdown__pointer-top', `${yPos}px`);
        break;
    }
  }
  render() {
    return [
      h("slot", { name: "anchor" }),
      h("ispui-dropdown-popup", { ISPUIDropdownEl: this, class: this.popupClass || 'ispui-dropdown-popup-stub-class', isClose: this.isClose, hasPointer: this.hasPointer, direction: this.currentDirection, onCloseClicked: () => {
          this.handleCloseClick();
        }, onMouseEnter: () => {
          this.handleMouseEnter();
        }, onMouseLeave: () => {
          this.handleMouseLeave(true);
        }, ref: el => (this.instance = el), zIndex: this.zIndex }),
    ];
  }
  get hostEl() { return this; }
  static get watchers() { return {
    "hoverDebounce": ["validateHoverDebounce"],
    "type": ["updateToggleTriggerListeners"],
    "disabled": ["setDisabledTriggers"],
    "direction": ["validateDirection"]
  }; }
}, [4, "ispui-dropdown", {
    "isClose": [4, "is-close"],
    "hasPointer": [4, "has-pointer"],
    "closeAfterClick": [4, "close-after-click"],
    "strictDirection": [4, "strict-direction"],
    "disabled": [4],
    "type": [1],
    "direction": [1],
    "directionOrder": [16],
    "alignment": [1],
    "alignmentOrder": [16],
    "zIndex": [2, "z-index"],
    "offset": [2],
    "hoverDebounce": [2, "hover-debounce"],
    "borderOffset": [2, "border-offset"],
    "pointerHeight": [2, "pointer-height"],
    "pointerWidth": [2, "pointer-width"],
    "pointerOffset": [2, "pointer-offset"],
    "popupClass": [1, "popup-class"],
    "closeOnOuterClick": [4, "close-on-outer-click"],
    "parentSelector": [1, "parent-selector"],
    "scrollSelector": [1, "scroll-selector"],
    "startAutoOffset": [2, "start-auto-offset"],
    "currentDirection": [32],
    "currentAlignment": [32],
    "showDropdown": [64],
    "getPopup": [64],
    "closeDropdown": [64],
    "setPointerPositionCallback": [64],
    "setupDropdown": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["ispui-dropdown", "ispui-close", "ispui-dropdown-popup"];
  components.forEach(tagName => { switch (tagName) {
    case "ispui-dropdown":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ISPUIDropdown);
      }
      break;
    case "ispui-close":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "ispui-dropdown-popup":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const IspuiDropdown = ISPUIDropdown;
const defineCustomElement = defineCustomElement$1;

export { IspuiDropdown, defineCustomElement };
